/**
 * ArchiMate 3.2 Relationship Validation Rules
 * Based on The Open Group ArchiMate 3.2 Specification
 */

import type { ElementType, RelationshipType } from './types';

/**
 * Validates if a relationship is allowed between two element types
 * according to ArchiMate 3.2 specification
 */
export function isValidRelationship(
  sourceType: ElementType,
  targetType: ElementType,
  relationshipType: RelationshipType
): boolean {
  // Get element category (active, behavior, passive, composite, motivation)
  const sourceCategory = getElementCategory(sourceType);
  const targetCategory = getElementCategory(targetType);

  // Apply ArchiMate relationship rules
  switch (relationshipType) {
    case 'composition':
    case 'aggregation':
      // Structural relationships - generally within same layer
      return true; // Simplified for now - can be refined

    case 'assignment':
      // Active structure to behavior
      return sourceCategory === 'active' && targetCategory === 'behavior';

    case 'realization':
      // Behavior/structure realizing behavior/structure
      return true; // Common relationship

    case 'serving':
      // Behavior serving behavior
      return true;

    case 'access':
      // Behavior accessing passive
      return targetCategory === 'passive';

    case 'influence':
      // Motivation elements influencing anything
      return sourceCategory === 'motivation';

    case 'triggering':
    case 'flow':
      // Dynamic relationships between behaviors
      return sourceCategory === 'behavior' && targetCategory === 'behavior';

    case 'association':
      // Generic association - allowed between most elements
      return true;

    case 'specialization':
      // Same type specialization
      return sourceType === targetType;

    default:
      return false;
  }
}

function getElementCategory(type: ElementType): string {
  // Active Structure Elements
  const active: ElementType[] = [
    'business-actor', 'business-role', 'business-collaboration', 'business-interface',
    'application-component', 'application-collaboration', 'application-interface',
    'node', 'device', 'system-software', 'technology-collaboration', 'technology-interface',
    'equipment', 'facility', 'distribution-network',
  ];

  // Behavior Elements
  const behavior: ElementType[] = [
    'business-process', 'business-function', 'business-interaction', 'business-event', 'business-service',
    'application-function', 'application-interaction', 'application-process', 'application-event', 'application-service',
    'technology-function', 'technology-process', 'technology-interaction', 'technology-event', 'technology-service',
  ];

  // Passive Structure Elements
  const passive: ElementType[] = [
    'business-object', 'contract', 'representation', 'product',
    'data-object',
    'artifact', 'material',
  ];

  // Motivation Elements
  const motivation: ElementType[] = [
    'stakeholder', 'driver', 'assessment', 'goal', 'outcome',
    'principle', 'requirement', 'constraint', 'meaning', 'value',
  ];

  if (active.includes(type)) return 'active';
  if (behavior.includes(type)) return 'behavior';
  if (passive.includes(type)) return 'passive';
  if (motivation.includes(type)) return 'motivation';

  return 'composite';
}

/**
 * Get all valid relationship types for a given source and target
 */
export function getValidRelationships(
  sourceType: ElementType,
  targetType: ElementType
): RelationshipType[] {
  const allTypes: RelationshipType[] = [
    'composition', 'aggregation', 'assignment', 'realization',
    'serving', 'access', 'influence', 'association',
    'triggering', 'flow', 'specialization',
  ];

  return allTypes.filter(relType =>
    isValidRelationship(sourceType, targetType, relType)
  );
}
